import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFetcher, useLoaderData } from 'react-router';
import { OS } from 'types';

import BellOutlineThick from '~/assets/icons/BellOutlineThick';
import CalendarIcon from '~/assets/icons/Calendar';
import RestockIcon from '~/assets/icons/Restock';
import SaleIcon from '~/assets/icons/Sale';
import { Button } from '~/components/Button';
import Image, { smallImageOptions } from '~/components/Image';
import ModalNav from '~/components/Modals/ModalNav';
import SetNotificationEvent from '~/events/SetNotificationEvent';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import gtm from '~/services/gtm.client';
import type { UserActions } from '~/user/Actions';
import { getPageViewId } from '~/utils/navigationContext';

import { Dialog } from '../../Dialog';
import type { NotificationData } from '../../ProductNotificationButton';
import Modals from '..';
import CancelNotifications from '../Login/NotificationFlow/CancelNotifications';
import AppNudge from './AppNudge';
import SizeGrid from './SizeGrid';

function getNotifyButtonText(selectedSizes: string[]) {
  if (selectedSizes.includes('ALL')) return 'Notify me on all UK sizes';

  return selectedSizes.length === 1
    ? `Notify me on UK size ${selectedSizes[0]}`
    : 'Notify me on multiple UK sizes';
}

function getSavedSizes(
  alerts: UserActions['alerts'] | undefined,
  productId: number
) {
  return (
    alerts?.products?.find(({ id }) => productId === Number(id))?.sizes ?? []
  );
}

const VerticalLine = () => (
  <div className="flex-none grow-0 self-stretch border-l border-concrete" />
);

const Panel = ({
  children,
  text
}: {
  children?: JSX.Element;
  text: string;
}) => {
  return (
    <div className="flex flex-none grow flex-row items-center justify-center gap-1 px-4 py-2">
      {children}
      <p className="text-sm font-normal leading-5">{text}</p>
    </div>
  );
};

type Props = {
  isOpen: boolean;
  onDismiss: () => void;
  product: NotificationData;
};

type FetcherData = {
  delete?: boolean;
  errors?: {
    authentication?: boolean;
  };
  sizes?: string[];
  success: boolean;
};

enum Stage {
  SIZE_GRID,
  LOGIN,
  APP_DOWNLOAD,
  CANCEL
}

export const ProductNotification = ({ product, onDismiss, isOpen }: Props) => {
  const user = useUser();
  const loaderData = useLoaderData();
  const fetcher = useFetcher<FetcherData>();
  const { isBot, clientOs, clientIp } = useRootData();
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [stage, setStage] = useState<Stage>(Stage.SIZE_GRID);

  const shouldShowIOSNudge =
    (loaderData as { shouldShowIOSNudge?: boolean })?.shouldShowIOSNudge ??
    false;

  const isAndroidClient = clientOs === OS.Android;

  const savedSizes = useMemo(
    () => getSavedSizes(user.actions?.alerts, product.id),
    [user.actions?.alerts, product.id]
  );

  useEffect(() => {
    setSelectedSizes(
      savedSizes.length ? savedSizes : [user.profile?.shoe_size ?? 'ALL']
    );
  }, [setSelectedSizes, savedSizes, user.profile?.shoe_size]);

  const onSetNotifications = useCallback(() => {
    new SetNotificationEvent({
      clientIp,
      isBot,
      legacyId: product.legacyId,
      os: clientOs,
      pagePosition: 'Modal',
      productId: product.id,
      productName: product.name,
      user
    }).send();
  }, [
    clientOs,
    isBot,
    product.legacyId,
    product.id,
    product.name,
    clientIp,
    user
  ]);

  const showAppDownload = useCallback(() => {
    if (!isAndroidClient && !shouldShowIOSNudge) setStage(Stage.APP_DOWNLOAD);
    else onDismiss();
  }, [isAndroidClient, onDismiss, shouldShowIOSNudge]);

  const onFullDismiss = useCallback(
    (options?: { loginSuccessful?: boolean }) => {
      if (options?.loginSuccessful) {
        onSetNotifications();
        showAppDownload();
      } else {
        setStage(Stage.SIZE_GRID);
        onDismiss();
      }
    },
    [onDismiss, onSetNotifications, showAppDownload]
  );

  useEffect(() => {
    if (!fetcher.data) return;

    const isNotificationSet =
      fetcher.data.success && !fetcher.data.delete && !savedSizes.length;
    const isAuthError =
      fetcher.data.errors?.authentication && !savedSizes.length;

    if (isNotificationSet) {
      onSetNotifications();
      showAppDownload();
    } else if (isAuthError) {
      setStage(Stage.LOGIN);
    } else {
      onFullDismiss();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher.data]);

  const loginPostAuthOptions = useMemo(
    () => ({
      action: 'restock' as const,
      productId: product.id.toString(),
      productName: product.name,
      sizes: selectedSizes
    }),
    [product.id, product.name, selectedSizes]
  );

  if (isOpen && stage === Stage.LOGIN) {
    return (
      <Modals.NotificationLogin
        onDismiss={onFullDismiss}
        product={product}
        postAuthOptions={loginPostAuthOptions}
      />
    );
  }

  if (stage === Stage.APP_DOWNLOAD) {
    return <Modals.AppDownload isOpen={true} onDismiss={onFullDismiss} />;
  }

  if (isOpen && stage === Stage.CANCEL) {
    return (
      <CancelNotifications
        image={product.image}
        onGoBack={() => setStage(Stage.SIZE_GRID)}
        onCancel={onFullDismiss}
      />
    );
  }

  return isOpen && stage === Stage.SIZE_GRID ? (
    <Dialog
      isOpen={true}
      onDismiss={() => {
        if (savedSizes.length > 0) {
          onDismiss();
        } else {
          gtm.trackedButtonClick({
            buttonName: 'X',
            pagePosition: 'Sign in modal'
          });
          setStage(Stage.CANCEL);
        }
      }}
      className="z-50 mx-4"
    >
      <fetcher.Form
        method="post"
        action={`/bolt/notification/${
          product.id
        }?selected=${JSON.stringify(selectedSizes)}&product_id=${product.id}`}
        className="flex min-w-fit flex-col items-center justify-center gap-4 rounded-lg bg-white p-4 md:w-[340px] lg:w-[456px] lg:gap-6 lg:p-6 xl:w-[552px]"
      >
        <input type="hidden" name="view_id" value={getPageViewId() ?? ''} />
        <div className="relative flex flex-col items-center justify-center self-stretch ">
          <ModalNav
            heading={`${savedSizes.length > 0 ? 'Edit' : 'Set'} notifications`}
          />
          {product.image && (
            <div className="flex size-24 items-center  justify-center lg:size-36">
              <Image
                src={product.image.url}
                alt={product.image.alt}
                loading="lazy"
                {...smallImageOptions({ pad: true })}
              />
            </div>
          )}
        </div>

        <div className="flex flex-col self-stretch">
          <div className="flex self-stretch">
            <VerticalLine />
            <Panel text="Release">
              <CalendarIcon width={22} height={22} stroke="#8907E7" />
            </Panel>
            <VerticalLine />
            <Panel text="Restocks">
              <RestockIcon width={24} height={24} stroke="#8907E7" />
            </Panel>
            <VerticalLine />
            <Panel text="Price drops">
              <SaleIcon width={24} height={24} stroke="#8907E7" />
            </Panel>
            <VerticalLine />
          </div>
        </div>

        <div className="flex w-full flex-col items-center gap-4">
          <div className="grid w-full grid-cols-7 gap-2">
            <SizeGrid
              selectedSizes={selectedSizes}
              setSelectedSizes={setSelectedSizes}
            />
          </div>
        </div>

        {savedSizes.length > 0 ? (
          <>
            <div className="flex grow-0 flex-row items-center justify-center gap-4 self-stretch p-0">
              <Button
                variant="red-alert-transparent"
                name="delete"
                value="true"
              >
                Turn off
              </Button>
              <Button variant="forest">Save</Button>
            </div>
          </>
        ) : (
          <Button className="h-12 w-full" variant="purple-hola">
            <div className="flex items-center justify-center gap-2">
              <BellOutlineThick />
              <p className="text-base text-white">
                {getNotifyButtonText(selectedSizes)}
              </p>
            </div>
          </Button>
        )}
        {!isAndroidClient && shouldShowIOSNudge && <AppNudge />}
      </fetcher.Form>
    </Dialog>
  ) : null;
};
